import * as React from 'react'

import { Box, Skeleton, theme } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { TeamViewDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { DateNavigator } from 'modules/core/components/DateNavigator'
import EmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters'
import {
  filterFields,
  getFilterValue,
} from 'modules/core/components/EmployeeMultiFilters/commonFilters'
import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import { useEmployeeGroups } from 'modules/core/components/MultiFilters/hooks/useEmployeeGroups'
import type { FilterOption as MultiFilterOption } from 'modules/core/components/MultiFilters/types'
import { useAsyncData, useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import Exports from 'modules/calendar/components/Exports'
import type { CalendarRootRenderProps } from 'modules/calendar/lib/RouteContextParams'
import generateFetchData from 'modules/calendar/lib/generateFetchData'
import calendarEvents from 'modules/calendar/store/collections/calendarEvents'
import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import TeamViewTable from './components/TeamViewTable'

type Props = CalendarRootRenderProps & {
  lastLeaveUpdatedInMilliseconds: number
  contractVersions: ContractVersions
  canCompanyCalendarAccess: boolean
}

const TeamViewPage = ({
  loading,
  children,
  date,
  range,
  onDateRangeChange,
  canSeeCalendar,
  canCompanyCalendarAccess,
  lastLeaveUpdatedInMilliseconds,
  contractVersions,
}: React.PropsWithChildren<Props>) => {
  const filterKey = LOCAL_STORAGE_KEYS.calendarFilters

  const {
    filteredModels: filteredEmployees,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
  } = useEmployeeMultiFilters<Employee>(getFilterValue, filterKey)

  const { loading: changingData } = useUnsafeAsyncData(() => {
    const data = generateFetchData(date, '', false)
    const ms = lastLeaveUpdatedInMilliseconds
    calendarEvents.reset([])

    return Promise.all([
      calendarEvents.fetch({
        data: { ...data, ms },
      }),
    ])
  }, [date, lastLeaveUpdatedInMilliseconds])

  const { employeeGroups } = useEmployeeGroups()
  const [fields, setFields] = React.useState<readonly MultiFilterOption[]>([])

  const { loading: loadingFilters } = useAsyncData(async () => {
    const fields = await filterFields(employeeGroups)

    setFields(fields)
  }, [])

  const [fetchWorkingDays, { data }] = useLazyQuery(TeamViewDocument)

  React.useEffect(() => {
    fetchWorkingDays({
      variables: {
        ids: filteredEmployees.map((employee) => employee.id),
        activeOn: dateToString(date, 'backend'),
      },
    })
  }, [filteredEmployees.length, date])

  const employeesWorkingDays = data
    ? data.employees.employees.reduce<Record<string, string>>((acc, { id, contractVersions }) => {
        acc[String(id)] = contractVersions[0]?.workingWeekDays ?? ''
        return acc
      }, {})
    : {}

  return (
    <ResponsivePage.Body
      authorized={canSeeCalendar}
      showHeader={false}
      loading={loading}
      error={null}
    >
      <Box gap="s16">
        {/* FIXME: Using a fixed height to avoid my OCD kicking in with the difference in height from the filter button to the date navigator.
        Should be automatically solved once we migrate to the new filter component. */}
        <Box flexDirection="row" justifyContent="spaceBetween" height="s32">
          {loadingFilters && <Skeleton width="s96" height="s32" />}
          {!loadingFilters && (
            <EmployeeMultiFilters
              label={i18n.t('filters.multi_filters.title')}
              fields={fields}
              filters={filters}
              logicOperator={logicOperator}
              onAddFilter={onAddFilter}
              onRemoveFilter={onRemoveFilter}
              onApplyFilters={(defaultFilter) =>
                onApplyFilters(employees.models, contractVersions, defaultFilter)
              }
              onCancel={onCancel}
              showDefaultFilters
              filterKey={LOCAL_STORAGE_KEYS.calendarFilters}
            />
          )}
          <Box gap="s16" flexDirection="row">
            <DateNavigator
              regularPeriods={['month']}
              startDate={range[0]}
              endDate={range[1]}
              onDateRangeChange={(...newRange) => onDateRangeChange(newRange)}
            />
            <Exports />
          </Box>
        </Box>
        <Box
          style={{
            width: '100%',
            height: 'calc(100vh - 200px)',
            marginBottom: `calc(${theme.dimensions.bodyPadding} * -1)`,
            overflow: 'auto',
            position: 'relative',
            zIndex: '1',
          }}
        >
          <TeamViewTable
            date={date}
            changingData={changingData}
            filteredEmployees={filteredEmployees}
            employeesWorkingDays={employeesWorkingDays}
          />
          {children}
        </Box>
      </Box>
    </ResponsivePage.Body>
  )
}

export default observer(TeamViewPage)
